import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { wordAudioId, type Beat } from '../content/beats';
import { play, playSequence, stopAudio, playEarcon } from '../audio/player';
import { chargeFill, wpm } from '../engine/fluency';
import { Sprite } from './Sprite';
import { SpeedMeter } from './SpeedMeter';

const MAX_ATTEMPTS = 3;

interface Props {
  beat: Beat;
  /** His fastest prior tap-through for this beat (ms), if any. */
  prevBestMs?: number;
  /** Fired when an attempt completes (parent records it + grants the fixed bonus once). */
  onAttempt: (timeMs: number, wordCount: number) => void;
  onClose: () => void;
}

type Phase = 'model' | 'read' | 'result';

/** Optional repeated-reading round (Phase 5 fluency). Spidey MODELS the corrected line,
 *  then the child REREADS it by tapping each word in order (timed = a WCPM proxy; we have
 *  no speech recognition by design). Beat-your-own-best is the motivator. Gentle + capped
 *  at 3 rereads, then move on. Speed never bypasses decoding — this runs only AFTER the
 *  decode loop, and the coin bonus (granted by the parent) is fixed, not speed-scaled. */
export function SpeedRun({ beat, prevBestMs, onAttempt, onClose }: Props) {
  const words = beat.words.map((w, i) => (i === beat.brokenIndex ? beat.correctWord : w));

  const [phase, setPhase] = useState<Phase>('model');
  const [attempt, setAttempt] = useState(1);
  const [expected, setExpected] = useState(0); // next word index to tap
  const [readIndex, setReadIndex] = useState<number | null>(null); // model highlight
  const [wrongAt, setWrongAt] = useState<number | null>(null); // out-of-order shake
  const [last, setLast] = useState<{ ms: number; fill: number; caption: string; good: boolean } | null>(null);

  const startedAt = useRef(0);
  const bestMs = useRef<number | undefined>(prevBestMs);

  // Model the line on entering the model phase (audio is already unlocked — he tapped the
  // Speed Run button to get here). Then hand over to him; the timer starts on his first tap.
  useEffect(() => {
    if (phase !== 'model') return;
    let live = true;
    void (async () => {
      await playSequence(words.map(wordAudioId), { gapMs: 150, onClipStart: (i) => setReadIndex(i) });
      if (!live) return;
      setReadIndex(null);
      setPhase('read');
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, attempt]);

  useEffect(() => () => stopAudio(), []);

  const replayModel = () => {
    setReadIndex(null);
    void (async () => {
      await playSequence(words.map(wordAudioId), { gapMs: 150, onClipStart: (i) => setReadIndex(i) });
      setReadIndex(null);
    })();
  };

  const tap = (i: number) => {
    if (phase !== 'read') return;
    if (i !== expected) {
      setWrongAt(i);
      setTimeout(() => setWrongAt(null), 450);
      void play(wordAudioId(words[i])); // read the tapped word, then nudge to read in order
      return;
    }
    if (expected === 0) startedAt.current = Date.now();
    const nextExpected = expected + 1;
    setExpected(nextExpected);
    if (nextExpected >= words.length) finish();
  };

  const finish = () => {
    const ms = Date.now() - startedAt.current;
    const firstEver = bestMs.current === undefined;
    const beatIt = !firstEver && ms < (bestMs.current as number);
    bestMs.current = firstEver ? ms : Math.min(bestMs.current as number, ms);
    const caption = firstEver
      ? 'Speedy reading! ⚡'
      : beatIt
        ? 'Faster than last time! ↑'
        : 'Nice reading! 🕸️';
    setLast({ ms, fill: chargeFill(wpm(words.length, ms)), caption, good: firstEver || beatIt });
    playEarcon('build');
    setPhase('result');
    onAttempt(ms, words.length);
  };

  const tryAgain = () => {
    setExpected(0);
    setReadIndex(null);
    setLast(null);
    setAttempt((a) => a + 1);
    setPhase('read');
  };

  return (
    <div style={overlay}>
      <div style={header}>
        <span style={title}>⚡ Speed Run</span>
        <span style={attemptTag}>Try {attempt} / {MAX_ATTEMPTS}</span>
        <button type="button" style={closeBtn} onClick={onClose} aria-label="Skip speed run">
          ✕
        </button>
      </div>

      <Sprite heroId="spidey" kind={phase === 'result' ? 'celebrate' : 'idle'} size={120} />

      <div style={hint}>
        {phase === 'model' && '🕷️ Listen to Spidey read it…'}
        {phase === 'read' && 'Now YOU read it — tap each word in order!'}
        {phase === 'result' && (last?.caption ?? '')}
      </div>

      <div style={card}>
        <div style={row}>
          {words.map((w, i) => {
            const done = phase === 'read' && i < expected;
            const isNext = phase === 'read' && i === expected;
            const modeling = phase === 'model' && readIndex === i;
            return (
              <motion.button
                key={i}
                type="button"
                onClick={() => tap(i)}
                disabled={phase !== 'read'}
                animate={wrongAt === i ? { x: [0, -6, 6, -4, 4, 0] } : { x: 0 }}
                whileTap={phase === 'read' ? { scale: 0.92 } : undefined}
                style={{ ...chip, ...(done ? chipDone : isNext ? chipNext : modeling ? chipModel : null) }}
              >
                {w}
              </motion.button>
            );
          })}
        </div>
      </div>

      {phase === 'result' && last && (
        <div style={resultWrap}>
          <SpeedMeter fill={last.fill} caption={last.caption} captionGood={last.good} />
          <div style={btnRow}>
            {attempt < MAX_ATTEMPTS && (
              <button type="button" style={againBtn} onClick={tryAgain}>
                Try again ⚡
              </button>
            )}
            <button type="button" style={doneBtn} onClick={onClose}>
              Done ✓
            </button>
          </div>
        </div>
      )}

      {phase === 'read' && (
        <button type="button" style={hearBtn} onClick={replayModel}>
          🔊 Hear Spidey
        </button>
      )}
    </div>
  );
}

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  background: 'var(--frame)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 14,
  padding: 20,
};
const header: CSSProperties = { position: 'absolute', top: 16, left: 18, right: 18, display: 'flex', alignItems: 'center', gap: 12 };
const title: CSSProperties = { fontSize: 24, fontWeight: 800, color: 'var(--gold)', flex: 1 };
const attemptTag: CSSProperties = { fontSize: 15, fontWeight: 800, color: '#cdd6ee' };
const closeBtn: CSSProperties = {
  width: 'var(--target-min)',
  height: 'var(--target-min)',
  borderRadius: '50%',
  border: 'none',
  background: 'var(--frame-2)',
  color: '#f4f4f4',
  fontSize: 22,
  cursor: 'pointer',
};
const hint: CSSProperties = { fontSize: 'var(--body-size)', color: '#cdd6ee', fontWeight: 700, textAlign: 'center', minHeight: 30 };
const card: CSSProperties = {
  background: 'var(--card)',
  borderRadius: 24,
  padding: '20px 24px',
  maxWidth: 880,
};
const row: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', alignItems: 'center' };
const chip: CSSProperties = {
  fontFamily: 'var(--font-read)',
  fontSize: 'clamp(26px, 5vmin, 46px)',
  fontWeight: 800,
  color: 'var(--ink)',
  background: '#fff',
  borderWidth: 3,
  borderStyle: 'solid',
  borderColor: 'var(--box-border)',
  borderRadius: 14,
  padding: '6px 16px',
  minHeight: 'var(--target-min)',
  cursor: 'pointer',
  lineHeight: 1.1,
};
const chipDone: CSSProperties = { color: '#fff', background: 'var(--good)', borderColor: 'var(--good)' };
const chipNext: CSSProperties = { borderColor: 'var(--gold)', background: 'var(--box-active-bg)' };
const chipModel: CSSProperties = { background: 'var(--box-active-bg)', borderColor: 'var(--box-active-border)', transform: 'translateY(-4px) scale(1.04)' };
const resultWrap: CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 };
const btnRow: CSSProperties = { display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' };
const againBtn: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 30px',
  borderRadius: 999,
  border: '2px solid var(--gold)',
  background: 'var(--frame-2)',
  color: 'var(--gold)',
  fontSize: 20,
  fontWeight: 800,
  cursor: 'pointer',
};
const doneBtn: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 38px',
  borderRadius: 999,
  border: 'none',
  background: 'var(--good)',
  color: '#fff',
  fontSize: 22,
  fontWeight: 800,
  cursor: 'pointer',
};
const hearBtn: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 22px',
  borderRadius: 999,
  borderWidth: 3,
  borderStyle: 'solid',
  borderColor: 'var(--gold)',
  background: 'transparent',
  color: 'var(--gold)',
  fontSize: 18,
  fontWeight: 800,
  cursor: 'pointer',
};
